import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { auth, signInWithCustomToken, onAuthStateChanged, signOut, signInWithEmailAndPassword, doc, database, onSnapshot } from "../Utils/firebaseConfig";
import { firebaseService } from "../Utils/firebaseService";
import isElectron from '../Utils/isElectron';
import { toast } from '@mobiscroll/react';
import { redirectToGoogleAuth, getGoogleOAuthRedirectUri, buildGoogleAuthUrl, getElectronGoogleOAuthParams } from "../Utils/googleAuth";
import { httpsCallable, functions } from "../Utils/firebaseConfig";
import {
  consumePendingGoogleOAuth,
  consumePendingGoogleOAuthError,
  clearGoogleOAuthInProgress,
  peekPendingGoogleOAuth,
} from "../Utils/pendingGoogleOAuth";
import { GOOGLE_OAUTH_READY } from "../Pages/OAuthCallback";

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // All original state variables
  const [user, setUser] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gCalAccessToken, setGcalAccessToken] = useState(null);
  const [gCalRefreshToken, setGcalRefreshToken] = useState(null);
  const [userCalendars, setUserCalendars] = useState([]);
  const [userSelectedCalendar, setUserSelectedCalendar] = useState("");
  const [isCompletingGoogleSignIn, setIsCompletingGoogleSignIn] = useState(false);
  const googleSignInStartedRef = useRef(false);

  const runPendingGoogleSignIn = useCallback(async () => {
    if (isElectron() || googleSignInStartedRef.current) return;
    const oauthError = consumePendingGoogleOAuthError();
    if (oauthError) {
      toast({ message: oauthError, color: "danger" });
      return;
    }

    const pending = consumePendingGoogleOAuth();
    if (!pending) return;

    googleSignInStartedRef.current = true;
    setIsCompletingGoogleSignIn(true);

    try {
      if (auth.currentUser) {
        const connectFn = httpsCallable(functions, "authenticateGoogleCalendar");
        await connectFn({
          code: pending.code,
          redirectUri: pending.redirectUri || getGoogleOAuthRedirectUri(),
        });
        toast({ message: "Google Calendar connected.", color: "success" });
        const syncFn = httpsCallable(functions, "syncCalendarNow", { timeout: 540000 });
        void syncFn({ target: "motionAir" }).catch((error) => {
          console.error("[AuthProvider] Post connect calendar sync failed:", error);
        });
      } else {
        const completeSignIn = httpsCallable(functions, "completeGoogleSignIn");
        const result = await completeSignIn({
          code: pending.code,
          redirectUri: pending.redirectUri || getGoogleOAuthRedirectUri(),
        });

        const { customToken } = result.data;
        if (!customToken) {
          throw new Error("Sign-in did not return a session token.");
        }

        await signInWithCustomToken(auth, customToken);
      }

      const syncFn = httpsCallable(functions, "syncCalendarNow", { timeout: 540000 });
      void syncFn({ target: "motionAir" }).catch((error) => {
        console.error("[AuthProvider] Post sign-in calendar sync failed:", error);
      });
    } catch (error) {
      console.error("[AuthProvider] Web Google sign-in failed:", error);
      toast({
        message: error.message || "Google sign-in failed.",
        color: "danger",
      });
    } finally {
      clearGoogleOAuthInProgress();
      setIsCompletingGoogleSignIn(false);
      googleSignInStartedRef.current = false;
    }
  }, []);

  // --- STABLE USEEFFECT HOOKS ---

  // EFFECT 1: Manages ONLY the Firebase authentication state
  useEffect(() => {
    // Unified auth state listener for both web and Electron
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // EFFECT 2: Manages fetching user data from Firestore AFTER authentication
  useEffect(() => {
    // This effect runs only when the `user` object changes (e.g., from null to a user)
    if (user) {
      // Inform Electron main of the signed-in identity (used for per-user notification filters)
      try {
        const fallbackEmail = (typeof window !== 'undefined') ? localStorage.getItem('electronEmail') : null;
        const identityEmail = user.email || fallbackEmail;
        if (isElectron() && window.electronAPI?.setUserIdentity && (identityEmail || user.displayName)) {
          window.electronAPI.setUserIdentity({
            uid: user.uid || null,
            email: identityEmail || null,
            displayName: user.displayName || user.name || (identityEmail ? identityEmail.split('@')[0] : null),
          });
        }
      } catch (_) {}

      const fallbackEmail = (typeof window !== 'undefined') ? localStorage.getItem('electronEmail') : null;
      const emailForSettings = user.email || fallbackEmail;
      if (!emailForSettings) {
        console.warn('[AuthProvider] No email available for user settings');
        setUserSettings(null);
        return undefined;
      }

      const unsubscribe = onSnapshot(
        doc(database, "user-settings", emailForSettings),
        (snap) => {
          setUserSettings(snap.exists() ? snap.data() : null);
        },
        (error) => {
          console.error("Error listening to user settings:", error);
          setUserSettings(null);
        }
      );

      return () => unsubscribe();
    } else {
      // If there is no user, ensure all user-specific data is cleared
      setUserSettings(null);
      setGcalAccessToken(null);
      setGcalRefreshToken(null);
      setUserCalendars([]);
      setUserSelectedCalendar("");
    }
  }, [user]); // This effect correctly depends only on the user object

  // Complete web Google OAuth after redirect (non-blocking — calendar loads ASAP)
  useEffect(() => {
    if (isElectron()) return;

    if (peekPendingGoogleOAuth()) {
      runPendingGoogleSignIn();
    }

    const onOAuthReady = () => runPendingGoogleSignIn();
    window.addEventListener(GOOGLE_OAUTH_READY, onOAuthReady);
    return () => window.removeEventListener(GOOGLE_OAUTH_READY, onOAuthReady);
  }, [runPendingGoogleSignIn]);

  const refreshUserSettings = useCallback(async () => {
    const fallbackEmail =
      typeof window !== "undefined" ? localStorage.getItem("electronEmail") : null;
    const emailForSettings = user?.email || fallbackEmail;
    if (!emailForSettings) {
      return;
    }
    try {
      const settings = await firebaseService.getUserSettings(emailForSettings);
      setUserSettings(settings);
    } catch (error) {
      console.error("[AuthProvider] refreshUserSettings failed:", error);
    }
  }, [user]);

  // --- END OF STABLE HOOKS ---

  const runElectronGoogleOAuth = useCallback(async ({ linkCalendar = false } = {}) => {
    if (!window.electronAPI?.openGoogleOAuth) {
      throw new Error("Electron OAuth is not available.");
    }

    const { clientId, redirectUri } = await getElectronGoogleOAuthParams(functions);
    const authUrl = buildGoogleAuthUrl({ clientId, redirectUri });
    const { code } = await window.electronAPI.openGoogleOAuth({ authUrl, redirectUri });

    if (linkCalendar) {
      const connectFn = httpsCallable(functions, "authenticateGoogleCalendar");
      await connectFn({ code, redirectUri });
      return { linked: true };
    }

    const completeSignIn = httpsCallable(functions, "completeGoogleSignIn");
    const { data } = await completeSignIn({ code, redirectUri });
    if (!data?.customToken) {
      throw new Error("Sign-in did not return a session token.");
    }

    await signInWithCustomToken(auth, data.customToken);
    if (data?.email && typeof window !== "undefined") {
      localStorage.setItem("electronEmail", data.email);
    }
    return data;
  }, []);

  // handleLogin now simply handles the sign-in, and lets the useEffects do the rest
  const handleLogin = async () => {
    console.log('[AuthProvider] handleLogin called');
    
    if (isElectron()) {
      try {
        await runElectronGoogleOAuth();
        await refreshUserSettings();
        kickCalendarSync("motionAir");
        toast({ message: "Successfully signed in!", color: "success" });
      } catch (error) {
        console.error('[AuthProvider] Error in Electron login:', error);
        toast({ message: error.message || "Authentication failed. Please try again.", color: "danger" });
      }
      return;
    }
    
    console.log('[AuthProvider] Web detected, redirecting to Google OAuth');
    try {
      await redirectToGoogleAuth(functions);
    } catch (error) {
      console.error('[AuthProvider] Web sign-in redirect failed:', error);
      toast({ message: error.message || "Sign-in failed. Please try again.", color: "danger" });
    }
  };

  // --- ALL OF YOUR OTHER FUNCTIONS, RESTORED AND UNCHANGED ---

  const revokeAccess = async () => {
    try {
      const { httpsCallable, functions } = await import('../Utils/firebaseConfig.js');
      const revoke = httpsCallable(functions, "revokeAppAccess");
      const { data } = await revoke();
      return data;
    } catch (error) {
      console.error("Error revoking access:", error);
      throw error;
    }
  };

  const syncCalendarNow = useCallback(async (target = "all") => {
    const fn = httpsCallable(functions, "syncCalendarNow", { timeout: 540000 });
    const { data } = await fn({ target });
    return data;
  }, []);

  const kickCalendarSync = useCallback((target = "motionAir") => {
    const fn = httpsCallable(functions, "syncCalendarNow", { timeout: 540000 });
    void fn({ target }).catch((error) => {
      console.error(`[AuthProvider] Background sync (${target}) failed:`, error);
    });
  }, []);

  const connectGoogleCalendar = useCallback(async () => {
    if (isElectron()) {
      await runElectronGoogleOAuth({ linkCalendar: true });
      await refreshUserSettings();
      kickCalendarSync("motionAir");
      return;
    }
    try {
      await redirectToGoogleAuth(functions);
    } catch (error) {
      console.error('[AuthProvider] Web calendar connect redirect failed:', error);
      toast({ message: error.message || "Sign-in failed. Please try again.", color: "danger" });
    }
  }, [refreshUserSettings, kickCalendarSync, runElectronGoogleOAuth]);

  const setRentmanSyncEnabled = useCallback(async (enabled) => {
    const fn = httpsCallable(functions, "setRentmanSyncEnabled");
    const { data } = await fn({ enabled });
    if (enabled && data?.success) {
      kickCalendarSync("rentman");
    }
    return data;
  }, [kickCalendarSync]);

  const setMotionAirSyncEnabled = useCallback(async (enabled) => {
    const fn = httpsCallable(functions, "setMotionAirSyncEnabled");
    const { data } = await fn({ enabled });
    return data;
  }, []);

  const updateSelectedCalendar = async (newCalendar) => {
    if (user && newCalendar !== "") {
      try {
        await firebaseService.updateUserSettings(user.email, { userSelectedCalendar: newCalendar });
      } catch (error) {
        console.error("Error updating calendar: ", error);
      }
    }
  };

  const handleLoginWithEmail = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      let message = error.message.replace('Firebase:', '');
      toast({ message: `${message}`, color: 'danger' });
      console.error(error);
    }
  };

  const handleLogout = () => {
    if (isElectron()) {
      localStorage.removeItem('electronUser');
    }
    setUser(null);
    setLoading(false);
    signOut(auth).catch((error) => console.log("Logout Error:", error));
  };
  
  // The complete context value object to be passed to children
  const value = {
    user,
    loading,
    userSettings,
    onLogin: handleLogin,
    handleLoginWithEmail,
    handleLogout,
    gCalAccessToken,
    gCalRefreshToken,
    setGcalAccessToken,
    setGcalRefreshToken,
    userCalendars,
    setUserCalendars,
    userSelectedCalendar,
    setUserSelectedCalendar,
    revokeAccess,
    updateSelectedCalendar,
    refreshUserSettings,
    connectGoogleCalendar,
    syncCalendarNow,
    kickCalendarSync,
    setRentmanSyncEnabled,
    setMotionAirSyncEnabled,
    isCompletingGoogleSignIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};